// @flow
import React from 'react';
import DocumentTitle from 'react-document-title';
import {
  Box,
  Button,
  Chip,
  Divider,
  Spinner,
  Stack,
  SubmitButton,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { PrintIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { ModalConfirm } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import {
  blue,
  green,
  lightGrey,
  warning,
} from '@fortress-technology-solutions/fortress-component-library/design/index';
import useRecertApplicationForm from '../hooks/useRecertApplicationForm';
import { FORM_ID } from '../formValidation';
import type { RecertApplicationRouteProps } from '../types';

const CHIP_SX = {
  saved: { color: green.dark, backgroundColor: green.lighter },
  // The palette's `orange` holds purple values; `warning` carries the amber badge pair.
  unsaved: {
    color: warning.badgeText,
    backgroundColor: warning.badgeBackground,
  },
};

const RecertApplicationForm = ({
  match,
  location,
}: RecertApplicationRouteProps) => {
  const {
    title,
    propertyName,
    closeLabel,
    saveLabel,
    printLabel,
    canPrint,
    isPrinting,
    ReactHookFormProps,
    schema,
    asyncOptions,
    isLoading,
    canSave,
    isSaving,
    saveStatus,
    saveStatusLabel,
    lastSavedText,
    save,
    requestClose,
    requestPrint,
    confirmation,
    isReadOnly,
  } = useRecertApplicationForm(match, location);

  return (
    <DocumentTitle title={title}>
      <Stack
        alignItems="center"
        width="100%"
        height="100vh"
        sx={{ overflow: 'hidden' }}
      >
        <Stack
          sx={{
            zIndex: 1000,
            backgroundColor: 'background.paper',
            paddingLeft: 5,
            paddingRight: 4,
            paddingY: 2,
            width: '100%',
            boxShadow: '0 2px 6px 4px rgba(0, 0, 0, 0.15)',
          }}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack>
            <Typography
              variant="headlineMedium"
              component="h1"
              sx={{ color: blue.darker, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography variant="titleLarge" sx={{ color: lightGrey.dark }}>
              {propertyName}
            </Typography>
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={2}>
            {saveStatus && (
              <Chip
                size="small"
                label={saveStatusLabel}
                sx={CHIP_SX[saveStatus]}
              />
            )}
            {lastSavedText && (
              <Typography variant="bodyMedium" sx={{ color: lightGrey.dark }}>
                {lastSavedText}
              </Typography>
            )}
            {!isReadOnly && (
              <SubmitButton
                variant="primary"
                disabled={!canSave}
                isSubmitting={isSaving}
                onClick={save}
              >
                {saveLabel}
              </SubmitButton>
            )}
            {/* A disabled button emits no hover events, so the span carries the tooltip. */}
            <Tooltip title={printLabel}>
              <Box component="span">
                {/* Same variant as Close, which gives the outlined look the design asks for. */}
                <Button
                  variant="primarySubtle"
                  color="primary"
                  aria-label={printLabel}
                  disabled={!canPrint}
                  onClick={requestPrint}
                >
                  {isPrinting ? <Spinner small /> : <PrintIcon />}
                </Button>
              </Box>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Button
              variant="primarySubtle"
              color="primary"
              onClick={requestClose}
            >
              {closeLabel}
            </Button>
          </Stack>
        </Stack>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            padding: 4,
            paddingTop: 6,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            width: { xs: '100%', md: '90%', lg: '80%', xl: '70%' },
          }}
        >
          <AsyncBox sx={{ height: '100%' }} loading={isLoading}>
            {isLoading ? (
              <div style={{ height: '100%' }} />
            ) : (
              <form id={FORM_ID} onSubmit={(event) => event.preventDefault()}>
                <Box paddingLeft={5.5}>
                  <Form
                    ReactHookFormProps={ReactHookFormProps}
                    fields={schema}
                    asyncOptions={asyncOptions}
                  />
                </Box>
              </form>
            )}
          </AsyncBox>
        </Box>
        {confirmation && (
          <ModalConfirm
            open
            title={confirmation.title}
            onClose={confirmation.onDismiss}
            actionsProps={[
              {
                variant: 'primarySubtle',
                children: confirmation.secondaryLabel,
                onClick: confirmation.onSecondary,
              },
              {
                variant: 'primary',
                children: confirmation.primaryLabel,
                onClick: confirmation.onPrimary,
                disabled: confirmation.isPrimaryDisabled,
              },
            ]}
            ContentProps={{ sx: { maxWidth: 560 } }}
          >
            <Typography variant="bodyMedium" component="div">
              {confirmation.body}
            </Typography>
          </ModalConfirm>
        )}
      </Stack>
    </DocumentTitle>
  );
};

export default RecertApplicationForm;
